import axios from '@/axiosInterceptor';
// import moment from 'moment';
import { buildLaneSearchQueryParams } from '@/common/buildSearchQueryParams';

const state = () => ({
  origin: {
    name: null,
    googlePlaceId: null,
    areaId: null,
    stateAreaId: null,
    kmaAreaId: null,
    active: true,
    searchText: '',
    showBadge: false,
    autocompleteResults: [],
  },
  destination: {
    name: null,
    googlePlaceId: null,
    areaId: null,
    stateAreaId: null,
    kmaAreaId: null,
    active: false,
    searchText: '',
    showBadge: false,
    autocompleteResults: [],
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  currentLane: {},
  laneSearchResults: [],
  filters: {},
  showPlanLimits: false,
  showNoExport: false,
  intercomSwLanes: true,
  filterSets: undefined,
  isOriginAutocompleteLoading: false,
  isDestinationAutocompleteLoading: false,
});

// getters
const getters = {};

// actions
const actions = {
  // get from the highway monitor areas autocomplete
  async getAutocomplete({ commit }, payload) {
    commit('setAutocompleteLoading', {
      value: true,
      isType: payload.isType,
    });
    try {
      let res = await axios.get(
        process.env.API_MONITOR +
          '/api/v1/areas/autocomplete?term=' +
          payload.term,
        { signal: payload.signal }
      );
      commit('setAutocompleteResults', {
        results: res.data,
        isType: payload.isType,
      });
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setAutocompleteLoading', {
        value: false,
        isType: payload.isType,
      });
    }
  },
  async getAreaById(_, id) {
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/areas/${id}`
      );
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // Update from a user clicking on a google place in an autocomplete
  updateFromGooglePlace({ commit }, payload) {
    const isType = payload.isType;
    const googlePlace = payload.googlePlace;

    commit('setFieldName', { isType: isType, name: googlePlace.description });
    commit('setFieldGooglePlaceId', {
      isType: isType,
      googlePlaceId: googlePlace.place_id,
    });
  },
  resetFields({ commit }, isType) {
    commit('resetField', isType);
  },
  // When a user clicks on a google autocomplete record
  async updateAreaIdFromGooglePlace({ state, commit }, isType) {
    let res = await axios.get(
      process.env.API_MONITOR +
        '/api/v1/areas/find_by_place_id?place_id=' +
        state[isType].googlePlaceId
    );
    commit('setAreaId', { isType: isType, area: res.data });
    return res;
  },
  // When a user clicks on a area on a map
  async updateFromAreaClicked({ commit }, payload) {
    const isType = payload.isType;
    const area = payload.area;

    commit('setFieldName', { isType: isType, name: area.name });
    commit('setAreaId', { isType: isType, area: area });
    commit('setFieldGooglePlaceId', { isType: isType, googlePlaceId: null });
  },
  // payload has the following attributes
  // destination_area_id
  // origin_area_id
  // q: {}
  async laneSearch(
    { commit, state },
    { query, userCompanyId, page, perPage, signal }
  ) {
    const { limits, usage } = this.state.users.currentUser.paywall ?? {};

    if (
      limits?.carrier_searches &&
      limits?.carrier_searches <= usage?.carrier_searches_count
    ) {
      this.commit('laneSearches/showPlanLimits');
      commit('setLaneSearchResults', []);
      commit('setPagination', null);
      return Promise.resolve({});
    }

    const body = {
      destination_area_id: query['destination_area_id'],
      origin_area_id: query['origin_area_id'],
      q: {
        ...buildLaneSearchQueryParams(state.filters, userCompanyId),
      },
      page,
      per_page: perPage,
    };
    try {
      let res = await axios.post(
        process.env.API_MONITOR + '/api/v1/carriers/search',
        body,
        { signal }
      );
      if (res && res?.data) {
        commit('setLaneSearchResults', res.data.carriers);
        commit('setCurrentLane', res.data.route);
        commit('setPagination', res?.headers);
      }
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else if (axios.isAxiosError(e) && status && [500].includes(status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  initializeStore({ commit }) {
    // const localStorageOrigin = localStorage.getItem('origin')
    // const localStorageDestination = localStorage.getItem('destination')
    if (window.location.href.includes('carriers/lane-search-results')) {
      if (
        localStorage.getItem('laneSearchResultsQueryUrl') ==
        window.location.href
      ) {
        const localStorageCurrentLane = localStorage.getItem('currentLane');
        const localStorageLaneSearchResults =
          localStorage.getItem('laneSearchResults');
        const localStorageLaneSearchResultsPagination = localStorage.getItem(
          'laneSearchResultsPagination'
        );

        if (localStorageCurrentLane && localStorageCurrentLane != 'undefined') {
          commit('setCurrentLane', JSON.parse(localStorageCurrentLane));
        }
        if (
          localStorageLaneSearchResults &&
          localStorageLaneSearchResults != 'undefined'
        ) {
          commit(
            'setInitialLaneSearchResults',
            JSON.parse(localStorageLaneSearchResults)
          );
        }
        if (
          localStorageLaneSearchResultsPagination &&
          localStorageLaneSearchResultsPagination != 'undefined'
        ) {
          commit(
            'setPagination',
            JSON.parse(localStorageLaneSearchResultsPagination)
          );
        }
      } else {
        localStorage.removeItem('currentLane');
        localStorage.removeItem('laneSearchResults');
        localStorage.removeItem('laneSearchResultsPagination');
      }
    }
  },
  clearStore({ commit }) {
    console.log('clearing laneSearches localStorage items');
    commit('setCurrentLane', {});
    localStorage.removeItem('currentLane');
    localStorage.removeItem('laneSearchResults');
    localStorage.removeItem('laneSearchResultsQueryUrl');
    localStorage.removeItem('laneSearchResultsPagination');
  },
  clearStoreCompletely({ commit }) {
    console.log('clearing laneSearches store completely');
    commit('setCurrentLane', {});
    commit('resetOriginAndDestination');
    localStorage.removeItem('currentLane');
    localStorage.removeItem('laneSearchResults');
    localStorage.removeItem('laneSearchResultsQueryUrl');
    localStorage.removeItem('laneSearchResultsPagination');
  },
  /**
   * Create a new filter set and commit it to the store.
   *
   * @param {Object} payload
   * @param {string} payload.owner_type - "company" or "user"
   * @param {string} payload.is_type - "lane_search" or "carrier_search"
   * @param {Array<number>} payload.classification_id_in - Filter by classification IDs
   * @returns {Promise<Object>} - The default filter set object
   */
  async createFilterSet(
    { commit },
    { owner_type, is_type, classification_id_in }
  ) {
    const body = {
      owner_type,
      is_type,
      filters: {
        classification_id_in,
      },
      default: true,
    };

    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/filter_sets`,
        body
      );
      commit('setFilterSets', res.data);
    } catch (e) {
      const status = e?.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      }
      throw e;
    }
  },
  /**
   * Update an existing filter set by ID.
   *
   * @param {number} payload.filter_set_id - ID of the filter set to update
   * @param {Array<number>} payload.classification_id_in - Updated classification IDs
   * @returns {Promise<Object>} - The default filter set object
   */
  async updateFilterSet({ commit }, { filter_set_id, classification_id_in }) {
    const body = {
      filters: {
        classification_id_in,
      },
    };

    try {
      const res = await axios.put(
        `${process.env.API_MONITOR}/api/v1/filter_sets/${filter_set_id}`,
        body
      );
      commit('setFilterSets', res.data);
    } catch (e) {
      const status = e?.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      }
      throw e;
    }
  },
  /**
   * Get the default filter set for a given owner.
   *
   * @param {'user'|'company'} payload.owner_type - Type of the owner
   * @param {number} payload.owner_id - ID of the owner
   * @param {'lane_search'|'carrier_search'} payload.is_type - Type of filter set
   * @returns {Promise<Object>} - The default filter set object
   */
  async getDefaultFilterSetForOwner(
    { commit },
    { owner_type, owner_id, is_type, preventCommit = false }
  ) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/filter_sets/default_for_owner`,
        {
          params: {
            owner_type,
            owner_id,
            is_type,
          },
        }
      );
      if (res.status === 404) return res;

      if (!preventCommit) {
        commit('setFilterSets', res.data);
      }

      return res;
    } catch (e) {
      const status = e?.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      }
      throw e;
    }
  },
};

// mutations
const mutations = {
  setAutocompleteResults(state, payload) {
    switch (payload.isType) {
      case 'origin':
        state.origin.autocompleteResults = payload.results;
        break;
      case 'destination':
        state.destination.autocompleteResults = payload.results;
        break;
    }
  },
  setFieldName(state, payload) {
    state[payload.isType].name = payload.name;
  },
  setFieldGooglePlaceId(state, payload) {
    state[payload.isType].googlePlaceId = payload.googlePlaceId;
  },
  // Set the area ids retured by the monitor service.  Called
  // after user clicks on an autocomplete result
  setAreaId(state, payload) {
    const area = payload.area;
    const isType = payload.isType;
    state[isType].areaId = area.id;
    state[isType].stateAreaId = area.state_area_id;
    state[isType].kmaAreaId = area.kma_area_id;
  },
  // reset a field
  resetField(state, isType) {
    localStorage.removeItem('laneSearchResults');
    localStorage.setItem(
      'laneSearchResultsQueryUrl',
      localStorage.getItem('laneSearchResultsQueryUrl') + isType + '-removed'
    );
    localStorage.removeItem('laneSearchResultsPagination');
    state.laneSearchResults = [];
    state.pagination = {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
    };
    state[isType] = {
      name: null,
      googlePlaceId: null,
      areaId: null,
      stateAreaId: null,
      kmaAreaId: null,
      showBadge: false,
      searchText: '',
    };
  },
  resetFieldAlerts(state, isType) {
    state[isType] = {
      active: true,
      name: null,
      googlePlaceId: null,
      areaId: null,
      stateAreaId: null,
      kmaAreaId: null,
      showBadge: false,
      searchText: '',
    };
  },
  resetOriginAndDestination(state) {
    state.origin = {
      name: null,
      googlePlaceId: null,
      areaId: null,
      stateAreaId: null,
      kmaAreaId: null,
      active: true,
      searchText: '',
      showBadge: false,
      autocompleteResults: [],
    };
    state.destination = {
      name: null,
      googlePlaceId: null,
      areaId: null,
      stateAreaId: null,
      kmaAreaId: null,
      active: false,
      searchText: '',
      showBadge: false,
      autocompleteResults: [],
    };
  },
  updateOrigin(state, payload) {
    state.origin = Object.assign(state.origin, payload);
    // localStorage.setItem('origin', JSON.stringify(state.origin))
  },
  updateDestination(state, payload) {
    state.destination = Object.assign(state.destination, payload);
    // localStorage.setItem('destination', JSON.stringify(state.destination))
  },
  setCurrentLane(state, lane) {
    localStorage.setItem('currentLane', JSON.stringify(lane));
    if (!lane) {
      state.currentLane = {};
    } else {
      state.currentLane = lane;
    }
  },
  setInitialLaneSearchResults(state, carriers) {
    if (
      localStorage.getItem('laneSearchResultsQueryUrl') == window.location.href
    ) {
      if (!carriers || carriers?.length == 0) {
        state.laneSearchResults = [];
      } else {
        state.laneSearchResults = carriers;
      }
    } else {
      state.laneSearchResults = [];
    }
  },
  setLaneSearchResults(state, carriers) {
    localStorage.setItem('laneSearchResults', JSON.stringify(carriers));
    if (window.location.href.includes('carriers/lane-search-results')) {
      localStorage.setItem('laneSearchResultsQueryUrl', window.location.href);
    }
    if (!carriers || carriers?.length == 0) {
      state.laneSearchResults = [];
    } else {
      state.laneSearchResults = carriers;
    }
  },
  setPagination(state, headers) {
    if (headers) {
      localStorage.setItem(
        'laneSearchResultsPagination',
        JSON.stringify(headers)
      );
      if (
        headers?.totalitems == 200 &&
        headers?.totalpages == 4 &&
        state.laneSearchResults?.length < 50
      ) {
        state.pagination = {
          currentPage: parseInt(headers.currentpage),
          totalPages: parseInt(headers.currentpage),
          totalRecords: parseInt(
            state.laneSearchResults?.length * headers.currentpage
          ),
        };
      } else {
        state.pagination = {
          currentPage: parseInt(headers.currentpage),
          totalPages: parseInt(headers.totalpages),
          totalRecords: parseInt(headers.totalitems),
        };
      }
    } else {
      localStorage.removeItem('laneSearchResultsPagination');
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
      };
    }
  },
  setNewRequestFilters(state, filters) {
    state.filters = filters;
  },
  showPlanLimits(state) {
    state.showPlanLimits = true;
  },
  hidePlanLimits(state) {
    state.showPlanLimits = false;
  },
  showNoExport(state) {
    state.showNoExport = true;
  },
  hideNoExport(state) {
    state.showNoExport = false;
  },
  setIntercomSwLanes(state, value) {
    state.intercomSwLanes = value;
  },
  setFilterSets(state, value) {
    state.filterSets = value;
  },
  setAutocompleteLoading(state, payload) {
    if (payload.isType === 'origin') {
      state.isOriginAutocompleteLoading = payload.value;
    } else {
      state.isDestinationAutocompleteLoading = payload.value;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
